import { t } from "i18next";
import queryString from "query-string";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { JobOfferPayload } from "../../models/candidate-action.model";
import { AppState } from "../../store/app-state";
import { createJobOffer } from "../../store/candidate/candidate-actions";
import { DEFAULT_ECOSYSTEM, workTypeOptions } from "./constants";
import { JobofferForm, JobofferQuery, SelectLabelValue } from "./context";
import { convertIdNameToLabelValue } from "../../utils/redux/interface-converter";
import { DEFAULT_CURRENCIES, DEFAULT_LANGUAGES } from "../../utils/constants";


const NOT_INFORMED_KEY = "private-home-page:jobOffer.modal.notInformed";

const useJobOffer = () => {
  const location = useLocation();
  const initialQuery = queryString.parse(location.search);
  const [queryJobOffer] = useState<JobofferQuery>(initialQuery);

  const dispatch = useDispatch();
  const { data: ecosystems } = useSelector(
    (state: AppState) => state.candidateState.candidateEcosystemsData
  );

  const { data: skillData } = useSelector(
    (state: AppState) => state.commonState.skillData
  );

  const { data: diversityData } = useSelector(
    (state: AppState) => state.commonState.diversityData
  );

  const { data: experienceTimeData } = useSelector(
    (state: AppState) => state.commonState.experienceTimeData
  );

  const { data: proficiencyData } = useSelector(
    (store: AppState) => store.commonState.proficiencyData
  );

  const { data: hireTypeData } = useSelector(
    (store: AppState) => store.commonState.hireTypeData
  );

  const languages = useMemo(() => {
    return DEFAULT_LANGUAGES;
  }, []);

  const currencies = useMemo(() => {
    return DEFAULT_CURRENCIES;
  }, []);

  const getEcosystem = useMemo(() => {
    if (queryJobOffer.ecosystem) {
      return ecosystems?.find((e) => e.id === queryJobOffer.ecosystem);
    }

    return DEFAULT_ECOSYSTEM;
  }, [queryJobOffer.ecosystem, ecosystems]);

  const getArray = useCallback((value?: string | string[]): string[] => {
    if (Array.isArray(value)) {
      return value;
    }
    return !!value ? [value] : [];
  }, []);

  const getAttributes = useCallback((
    attrData: any[], queryAttr?: string | string[]
  ): SelectLabelValue[] => {
    const queryAttrArray = getArray(queryAttr);
    
    const attributes = queryAttrArray
    .reduce((arr: SelectLabelValue[], attr) => {
      const attribute = attrData?.find((a) => a.id === attr);
      return attribute ? [...arr, convertIdNameToLabelValue(attribute)] : arr;
    }, []);

    return attributes;
  }, [getArray]);

  const getSkills = useMemo((): SelectLabelValue[] => {
    return getAttributes(skillData, queryJobOffer.skills);
  }, [queryJobOffer.skills, skillData, getAttributes]);

  const getHireType = useMemo((): SelectLabelValue[] => {
    return getAttributes(hireTypeData, queryJobOffer.hireTypes);
  }, [queryJobOffer.hireTypes, hireTypeData, getAttributes]);

  const getExperience = useMemo((): SelectLabelValue[]  => {
    return getAttributes(experienceTimeData, queryJobOffer.experience);
  }, [queryJobOffer.experience, experienceTimeData, getAttributes]);

  const getDiversity = useMemo((): SelectLabelValue[] => {
  return getAttributes(diversityData, queryJobOffer.diversity);
  }, [queryJobOffer.diversity, diversityData, getAttributes]);

  const getWorkType = useMemo((): SelectLabelValue[] => {
    if (queryJobOffer.workType) {
      if(Array.isArray(queryJobOffer?.workType)){
        const workTypes = queryJobOffer?.workType?.map((wt) => (
          workTypeOptions.find(
            (w) => w.value === wt
          )
        )).filter(Boolean).map(
          (wt) => ({...wt, label: wt ? t(wt.label) : ""})
        );
        return workTypes as SelectLabelValue[];
      }

      const value = queryJobOffer?.workType;
      const workTypeWithLabel = workTypeOptions.find(
        (wt) => wt.value === value
      );
      if (workTypeWithLabel){
        return [{...workTypeWithLabel, label: t(workTypeWithLabel.label)}]; 
      }
    }
    return []
  }, [queryJobOffer.workType]);

  const getLanguage = useMemo((): SelectLabelValue | undefined  => {
    const language = languages.find(
      (l) => l.value === queryJobOffer.language_id
    );
    return language;
  }, [languages, queryJobOffer.language_id]);

  const getLanguageLevel = useMemo((): SelectLabelValue[] => {
    const languageLevels = getArray(queryJobOffer.language_level);

    const languageLevelsArray = languageLevels
    .map((ll) => {
      const languageLevel = proficiencyData?.find((pl) => pl.key === ll);
      return languageLevel ? convertIdNameToLabelValue(languageLevel) : null;
    }).filter(Boolean) as SelectLabelValue[];

    return languageLevelsArray;
  }, [queryJobOffer.language_level, proficiencyData, getArray]);

  const getCurrency = useMemo((): SelectLabelValue => {
    const currency = currencies.find(
      (c) => c.value === queryJobOffer.currency
    );
    return currency ? currency : currencies[0];
  }, [queryJobOffer.currency, currencies])

  const initialState: JobofferForm = {
    ecosystem: getEcosystem,
    skills: getSkills,
    currency: getCurrency,
    salary: '',
    workType: getWorkType,
    hireTypes: getHireType,
    experience: getExperience,
    workplace: queryJobOffer?.workplace,
    diversity: getDiversity,
    language: getLanguage,
    language_level: getLanguageLevel,
    plusInfo: '',
  };

  const [jobofferform, setJobofferform] = useState<JobofferForm>(initialState);

  const onResetJobOffer = () => {
    setJobofferform(initialState);
  };

  const onCreateJobOffer = useCallback(() => {
    let job_offer: JobOfferPayload = {};
    if (jobofferform.ecosystem) {
      job_offer["main_skill"] = jobofferform.ecosystem.name;
    }

    if (jobofferform.workType) {
      job_offer["work_type"] = jobofferform.workType.map((wt) => wt.value);
    }

    if (jobofferform.hireTypes) {
      job_offer["hire_types"] = jobofferform.hireTypes.map((ht) => ht.value);
    }

    if (jobofferform.diversity) {
      job_offer["diversity"] = jobofferform.diversity.map((div) => div.value);
    }

    if (jobofferform.workplace) {
      job_offer["workplace"] = jobofferform.workplace;
    }

    if(jobofferform.currency) {
      job_offer["currency"] = jobofferform.currency.value;
    }

    if(jobofferform.language) {
      job_offer["language"] = jobofferform.language.value;
    }

    if(jobofferform.language_level) {
      const obj = [...jobofferform.language_level].reverse()[0];
      job_offer["language_level"] = obj?.value;
    }

    if(jobofferform.plusInfo) {
      job_offer["plus_info"] = jobofferform.plusInfo;
    }

    const numberSalary = Number(jobofferform.salary);
    if (!isNaN(numberSalary) && numberSalary > 0) {
      job_offer["salary"] = `${Number(jobofferform.salary).toFixed(2)}`;
    }

    dispatch(createJobOffer(job_offer));
  }, [dispatch, jobofferform]);

  const verifyJoinText = (list: any, map_function: (e: any) => any) => {
    return Array.isArray(list) && list.length > 0
      ? list.map(map_function).join(" - ")
      : t(NOT_INFORMED_KEY);
  };

  const updateJobOffer = () => {
    onCreateJobOffer();
  };

  const joinTextSkills = (skills: JobofferForm["skills"]) => {
    const filteredSkills = skills?.map((skill) =>
      skillData?.find((appSkill) => appSkill.id === skill.value)
    );
    return verifyJoinText(filteredSkills, (skill) => skill && skill.name);
  };

  const joinTextExperience = (experienceTimes: JobofferForm["experience"]) => {
    const filteredExperienceTimes = experienceTimes?.map((exp) =>
      experienceTimeData?.find((expOption) => expOption.id === exp.value)
    );
    return verifyJoinText(filteredExperienceTimes, (exp) => exp && exp.name);
  };

  const joinTextDiversity = (divs: JobofferForm["diversity"]) => {
    const filteredDiversities = divs?.map((div) =>
      diversityData?.find((divOption) => divOption.id === div.value)
    );
    return verifyJoinText(filteredDiversities, (div) => div && div.name);
  };

  const joinTextWorkType = (workTypes: JobofferForm["workType"]) => {
    return verifyJoinText(workTypes, (worktype) => worktype && worktype.label);
  };

  const joinTextCurrency = (currency: JobofferForm["currency"]) => {
    return currency 
      ? `${currency.value} (${currency.label})` 
      : t(NOT_INFORMED_KEY);
  };

  const joinTextLanguage = (language: JobofferForm["language"]) => {
    return language ? t(language.label) : t(NOT_INFORMED_KEY);
  };

  const joinTextLanguageLevel = (language_level: JobofferForm["language_level"]) => {
    const filteredLanguageLevel = language_level ? language_level.map((ll) =>
      proficiencyData?.find((llOption) => llOption.id === ll.value)
    ).reverse() : [];
    return filteredLanguageLevel[0]
      ? filteredLanguageLevel[0].name
      : t(NOT_INFORMED_KEY);
  };

  const joinTextSalary = (salary: JobofferForm["salary"]) => {
    return salary ? `${Number(salary).toFixed(2)}` : t(NOT_INFORMED_KEY);
  };

  useEffect(() => {
    if (jobofferform) {
      onCreateJobOffer();
    }
  }, [jobofferform, onCreateJobOffer]);

  return {
    jobofferform,
    joinTextDiversity,
    joinTextExperience,
    joinTextCurrency,
    joinTextLanguage,
    joinTextLanguageLevel,
    joinTextSalary,
    joinTextSkills,
    joinTextWorkType,
    onCreateJobOffer,
    setJobofferform,
    updateJobOffer,
    onResetJobOffer,
  };
};

export default useJobOffer;
